
import { BlogPost } from '@/types/blog';
import { WPQueryParams } from './types';
import { fetchWordPressPosts } from './api';
import { transformPost } from './transformers';

/**
 * Recupera post del blog da WordPress
 */
export const fetchPosts = async (params: WPQueryParams = {}): Promise<BlogPost[]> => {
  try {
    console.log('[WordpressAPI] Inizio recupero post con parametri:', JSON.stringify(params));
    
    // Assicurati che category venga passato correttamente come categories all'API
    if (params.category !== undefined) {
      console.log(`[WordpressAPI] Categoria originale: ${params.category} (${typeof params.category})`);
      
      // La proprietà category verrà convertita in categories nella funzione fetchWordPressPosts
      const categoryId = typeof params.category === 'string' 
        ? parseInt(params.category, 10) 
        : params.category;
        
      if (!isNaN(Number(categoryId)) && Number(categoryId) > 0) {
        console.log(`[WordpressAPI] Uso categoria numerica: ${categoryId}`);
      } else if (params.category === null || params.category === '') {
        console.log('[WordpressAPI] Categoria vuota o null, recupero tutti i post');
        params.category = undefined; // Assicuriamoci che sia undefined per recuperare tutti i post
      }
    } else {
      console.log('[WordpressAPI] Nessuna categoria specificata, recupero tutti i post');
    }
    
    // Gestione del parametro di ricerca
    if (params.search) {
      console.log(`[WordpressAPI] Ricerca per termine: "${params.search}"`);
    }
    
    // Passa i parametri di query alla funzione API
    const wpPosts = await fetchWordPressPosts(params);
    console.log(`[WordpressAPI] Ricevuti ${wpPosts.length} post grezzi da WordPress`);
    
    // Trasforma i post WordPress nel formato BlogPost
    const transformedPosts = wpPosts.map((post: any) => transformPost(post));
    
    // Debug output per i post trasformati
    if (transformedPosts.length > 0) {
      console.log(`[WordpressAPI] Post trasformati - primo post:`, {
        id: transformedPosts[0].id,
        titolo: transformedPosts[0].title,
        categorie: transformedPosts[0].categories,
        categoryIds: transformedPosts[0].categoryIds,
        slug: transformedPosts[0].slug
      });
    }
    
    return transformedPosts;
  } catch (error) {
    console.error('[WordpressAPI] Errore nel recupero e trasformazione dei post:', error);
    return [];
  }
};

/**
 * Recupera un singolo post tramite slug
 */
export const fetchPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    // Re-export from api.ts with transformations
    const { fetchWordPressPostBySlug } = await import('./api');
    const wpPost = await fetchWordPressPostBySlug(slug);
    
    if (wpPost) {
      return transformPost(wpPost);
    }
    
    return null;
  } catch (error) {
    console.error(`Errore nel recupero del post con slug "${slug}":`, error);
    return null;
  }
};

/**
 * Recupera categorie da WordPress API
 */
export const fetchCategories = async () => {
  try {
    // Re-export from api.ts with transformations
    const { fetchWordPressCategories } = await import('./api');
    const categories = await fetchWordPressCategories();
    
    // Normalizza i dati delle categorie
    return categories.map((cat: any) => ({
      id: Number(cat.id), // Assicura che ID sia un numero
      name: cat.name,
      count: cat.count || 0
    })) || [];
  } catch (error) {
    console.error('Errore nel recupero delle categorie:', error);
    return [];
  }
};

/**
 * Recupera tag da WordPress API
 */
export const fetchTags = async (): Promise<string[]> => {
  try {
    // Re-export from api.ts with transformations
    const { fetchWordPressTags } = await import('./api');
    const tags = await fetchWordPressTags();
    
    return tags.map((tag: any) => tag.name) || [];
  } catch (error) {
    console.error('Errore nel recupero dei tag:', error);
    return [];
  }
};
